import { Dropdown, Tag, Tooltip, Typography } from "antd"

import Button from "../../../../Components/Button"
import InfoTooltip from "../../../../Components/InfoTooltip/InfoTooltip"
import { titleSortFilters } from "../../../../Components/titleSortFilters"
import Icon from "../../../../Icon"
import { formatCurrency } from "../../../../Utils/utils"
import { buildFieldDefs } from "../../../DisputesPage/pages/AllPage/components/DisputesTable/columns"
import { ADMIN_COLUMN_ICONS, ADMIN_SORTABLE } from "../../constants/columns"
import { PAID_STATUS_COLORS, PAID_STATUS_LABELS } from "../../constants/paidStatus"

const { Text, Link } = Typography

const header = (t) => (label, key) => (state) =>
  titleSortFilters({
    title: t(label),
    icon: ADMIN_COLUMN_ICONS[key],
    sorter: ADMIN_SORTABLE.has(key),
    key,
    stateData: state
  })

// The account block: who the dispute belongs to. Everything else on this table is the
// merchant list's own column, reused from its field defs rather than restated here.
const buildAccountFieldDefs = ({ t, onSelectAccount }) => {
  const makeHeader = header(t)

  return {
    user_full_name: {
      title: makeHeader("User full name", "user_full_name"),
      dataIndex: "user_full_name",
      key: "user_full_name",
      width: 190,
      sorter: ADMIN_SORTABLE.has("user_full_name"),
      render: (v) => <Text ellipsis={{ tooltip: v }}>{v || t("N/A")}</Text>
    },
    user_email: {
      title: makeHeader("User email", "user_email"),
      dataIndex: "user_email",
      key: "user_email",
      width: 220,
      sorter: ADMIN_SORTABLE.has("user_email"),
      render: (v) => <Text ellipsis={{ tooltip: v }}>{v || t("N/A")}</Text>
    },
    // The same two choices the Alerts table offers on an account: its own settings, or the
    // merchant's dashboard in the admin user view.
    business_account: {
      title: makeHeader("Business account", "business_account"),
      dataIndex: "business_account",
      key: "business_account",
      width: 200,
      sorter: ADMIN_SORTABLE.has("business_account"),
      render: (v, row) =>
        v ? (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                { key: "business_account", label: t("Business account") },
                { key: "dashboard", label: t("Users view") }
              ],
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation()
                onSelectAccount(row, key)
              }
            }}
          >
            <Link onClick={(e) => e.stopPropagation()} ellipsis title={v}>
              {v}
            </Link>
          </Dropdown>
        ) : (
          <Text type="secondary">·</Text>
        )
    },
    business_website: {
      title: makeHeader("Business URL", "business_website"),
      dataIndex: "business_website",
      key: "business_website",
      width: 210,
      render: (v) =>
        v ? (
          <Link
            href={v}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            ellipsis
            title={v}
          >
            {v.replace(/^https?:\/\//, "")}
          </Link>
        ) : (
          <Text type="secondary">·</Text>
        )
    },
    // Our success fee on this dispute. Only a win incurs one, so anything else has nothing
    // to pay and says so rather than showing a misleading "Unpaid".
    paid_status: {
      title: makeHeader("Paid status", "paid_status"),
      dataIndex: "paid_status",
      key: "paid_status",
      width: 150,
      sorter: ADMIN_SORTABLE.has("paid_status"),
      render: (v) =>
        v ? (
          <Tag className="dispute-tag" color={PAID_STATUS_COLORS[v]}>
            {t(PAID_STATUS_LABELS[v])}
          </Tag>
        ) : (
          <Tooltip title={t("A dispute fee is only charged on disputes we win.")}>
            <Text type="secondary">·</Text>
          </Tooltip>
        )
    },
    // An unpaid win is still owed, so it shows nothing rather than a 0 that would read as
    // "nothing to pay"; a loss owes nothing at all and says so on hover.
    paid_amount: {
      title: makeHeader("Paid amount", "paid_amount"),
      dataIndex: "paid_amount",
      key: "paid_amount",
      width: 150,
      align: "right",
      sorter: ADMIN_SORTABLE.has("paid_amount"),
      render: (v, row) =>
        v == null ? (
          <Tooltip
            title={
              row?.status === "won"
                ? t("The dispute fee on this win has not been paid yet.")
                : t("A dispute fee is only charged on disputes we win.")
            }
          >
            <Text type="secondary">·</Text>
          </Tooltip>
        ) : (
          <Text>{formatCurrency(Number(v) || 0)}</Text>
        )
    }
  }
}

// field_id -> antd column. The merchant list's defs first, the account block on top of them.
// `members`/`onAssign` are the Assignee column's dependencies; this table has no Assignee
// column, so they stay empty.
export const buildAdminFieldDefs = ({ t, onSelectAccount }) => ({
  ...buildFieldDefs({
    t,
    members: [],
    labelForCategory: (v) => v,
    onAssign: () => {}
  }),
  ...buildAccountFieldDefs({ t, onSelectAccount })
})

const makeDefaultColumn = (t) => (c) => ({
  title: t(c.label),
  dataIndex: c.field_id,
  key: c.field_id,
  width: 140,
  render: (v) =>
    v == null || v === "" ? <Text type="secondary">·</Text> : <Text>{String(v)}</Text>
})

// Pin, then the active columns in the order Edit columns holds them. `#` and Status stay
// frozen at the left, so any fixed-left column is forced to the front of the data columns
// regardless of where the admin dragged it (Array.prototype.sort is stable, so the rest
// keep their saved order).
export const buildAdminColumns = ({ t, tableColumns, fieldDefs, onPin }) => {
  const getDefaultColumn = makeDefaultColumn(t)

  const pinCol = {
    key: "pin",
    dataIndex: "pinned",
    width: 64,
    fixed: "left",
    title: <InfoTooltip title={t("Pin a dispute to keep it pinned to the top of the list.")} />,
    render: (_, row) => (
      <Tooltip title={row.pinned ? t("Unpin") : t("Pin to top")}>
        <Button
          type="text"
          shape="circle"
          className="disputes-pin-btn"
          aria-label={row.pinned ? t("Unpin") : t("Pin to top")}
          icon={<Icon type={row.pinned ? "pin_active" : "pin"} />}
          onClick={(e) => {
            e.stopPropagation()
            onPin(row)
          }}
        />
      </Tooltip>
    )
  }

  const numberCol = {
    key: "number",
    dataIndex: "number",
    width: 72,
    fixed: "left",
    sorter: ADMIN_SORTABLE.has("number"),
    title: (state) =>
      titleSortFilters({
        title: null,
        icon: "hashtag",
        sorter: true,
        key: "number",
        stateData: state
      }),
    render: (v) => <Text style={{ fontVariantNumeric: "tabular-nums" }}>{v}</Text>
  }

  const active = (tableColumns || []).filter((c) => c.active)
  const dataCols = active
    .filter((c) => c.field_id !== "number")
    .map((c) => fieldDefs[c.field_id] || getDefaultColumn(c))
  const orderedDataCols = [...dataCols].sort(
    (a, b) => (b.fixed === "left" ? 1 : 0) - (a.fixed === "left" ? 1 : 0)
  )

  const numberActive = active.some((c) => c.field_id === "number")
  return [pinCol, ...(numberActive ? [numberCol] : []), ...orderedDataCols]
}
